import React, { useEffect, useState } from "react";

import Header from "components/Header";
import MainLayout from "layouts/Main";
import styles from "./style.module.scss";
import QueryPostedTruckItem from "../modules/QueryPostedItem";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { QUERY_TRUCK_DETAIL } from "../Detail/query";
import { ITruckData } from "../../../../models/Truck";
import { convertDiffToAge } from "../../../../helpers/convertFormData";
import dayjs from "dayjs";
import { QUERY_LOADS_MATCH_TO_TRUCK } from "./query";
import { handleLoading } from "../../../../store/global/loader";
import { showErrorMessage } from "../../../../helpers/toastMessage";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../store";
import { LOAD_SEARCH_HEADER_LIST } from "../../../../constants/loads";
import DataTable from "../../../../components/DataTable";
import { routes } from "../../../../constants/routes";
import { IQueryLoadData } from "../../../../models/Load";
import usePaginatedQuery from "../../../../hooks/usePaginatedQuery";
import useDeviceType from "../../../../hooks/useDeviceType";
import LoadPostedItem from "../../Loads/modules/LoadPostedItem/LoadPostedItem";

export default function TruckMatchesList() {
  const navigate = useNavigate();
  const { truckId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const { isDesktop } = useDeviceType();

  const {
    data,
    loading: truckLoading,
    error,
    refetch
  } = useQuery(QUERY_TRUCK_DETAIL, {
    variables: { id: truckId }
  });

  const {
    data: matchesListData,
    loading: matchesListLoading,
    error: matchesListError,
    loadNextPage,
    pageInfo,
    totalCount,
    goToPage,
    loadPreviousPage,
    setPageSize
  } = usePaginatedQuery<any, any>({
    query: QUERY_LOADS_MATCH_TO_TRUCK,
    variables: { truckId: truckId },
    cursorField: "loadsMatchToTruck",
    pageSize: 20,
    options: {
      notifyOnNetworkStatusChange: true
    }
  });

  const [truckData, setTruckData] = useState<
    ITruckData & {
      truckAge: string;
      truckAddress: string;
      truckCoords: google.maps.LatLngLiteral[];
    }
  >();

  const [loadsData, setLoadsData] = useState<IQueryLoadData[]>();

  useEffect(() => {
    dispatch(handleLoading(truckLoading));

    if (!truckLoading && error) {
      showErrorMessage(error.message);
    }
  }, [truckLoading]);

  useEffect(() => {
    if (!matchesListLoading && matchesListError) {
      showErrorMessage(matchesListError.message);
    }
  }, [matchesListLoading]);

  useEffect(() => {
    if (data?.truck) {
      const truck = data.truck;
      const truckAge = convertDiffToAge(dayjs().diff(dayjs(truck.timePosted), "milliseconds"));
      const truckAddress = [truck.user.city, truck.user.state, truck.user.country]
        .filter((item) => !!item && item !== undefined)
        .join(", ");
      const truckCoords = [];
      if (truck.originCoords)
        truckCoords.push({ lat: truck.originCoords.latitude, lng: truck.originCoords.longitude });
      if (truck.destinationCoords)
        truckCoords.push({
          lat: truck.destinationCoords.latitude,
          lng: truck.destinationCoords.longitude
        });

      setTruckData({ ...truck, truckAge, truckAddress, truckCoords });
    }
  }, [data]);

  useEffect(() => {
    const newLoads = matchesListData?.map((item: any, index: any) => ({
      ...item,
      timeStart: {
        timeStart: item.timeStart,
        startDay: dayjs(item.timeStart).format("DD MMM"),
        startTime: dayjs(item.timeStart).format("hh:mm A")
      },
      timeEnd: {
        timeEnd: item.timeEnd,
        endDay: dayjs(item.timeEnd).format("DD MMM"),
        endTime: dayjs(item.timeEnd).format("hh:mm A")
      },
      timeDelivery: {
        timeDelivery: item.timeDelivery,
        deliveryDay: dayjs(item.timeDelivery).format("DD MMM"),
        deliveryTime: dayjs(item.timeDelivery).format("hh:mm A")
      },
      index
    }));
    setLoadsData(newLoads);
  }, [matchesListData]);

  const goLoadDetails = (id: string) => {
    navigate(`${routes.LOADS_SEARCH}/${id}`);
  };

  function goTruckDetails(id: string) {
    navigate(`${routes.TRUCKS_MY_POSTED}/${id}`);
  }

  function editTruckItem(id: string) {
    navigate(`${routes.TRUCKS_POST}/${id}`);
  }

  function goMatches() {
    location.reload();
  }

  const refresh = async () => {
    await refetch();
  };

  return (
    <MainLayout
      desktopTitle="Post Truck"
      className="px-5 pb-24"
      customHeader={<Header label="Matches List" />}>
      <div className={`${styles.matchesListHeader} ${isDesktop && "px-3.5"}`}>
        {truckData && (
          <QueryPostedTruckItem
            data={truckData}
            goMatches={goMatches}
            goDetail={goTruckDetails}
            editTruckItem={editTruckItem}
            refresh={refresh}
          />
        )}
      </div>

      {loadsData?.length && !truckLoading ? (
        isDesktop ? (
          <div className={styles.matchesListBody}>
            <DataTable
              className={styles.matchesListList}
              headers={LOAD_SEARCH_HEADER_LIST}
              data={loadsData.map((data: any) => ({
                ...data,
                originData: {
                  originCity: data.originCity,
                  originState: data.originState
                },
                destinationData: {
                  destinationCity: data.destinationCity,
                  destinationState: data.destinationState
                },
                details: { action: () => goLoadDetails(data.id) }
              }))}
              sortDisableList={["details", "deadhead"]}
              totalCount={totalCount}
              nextLink={pageInfo.hasNextPage}
              prevLink={pageInfo.hasPreviousPage}
              setPageSize={setPageSize}
              goToPage={goToPage}
              fetchPrevPage={loadPreviousPage}
              fetchNextPage={loadNextPage}
              loading={matchesListLoading}
            />
          </div>
        ) : (
          <ul className={styles.loadList}>
            {loadsData.length > 0 &&
              loadsData.map((item) => (
                <LoadPostedItem
                  key={item.id}
                  data={item}
                  onClickDetails={goLoadDetails}
                  editable={false}
                />
              ))}
          </ul>
        )
      ) : (
        <></>
      )}
    </MainLayout>
  );
}
